/**
 * Pure maths for ProTimeline: zoom, pixel/time conversion, ruler ticks, row
 * packing for overlapping items, and snapping while dragging. Kept separate so
 * it can be unit-tested without a DOM.
 *
 * Zoom is expressed as pixels per second ("pps").
 */

import { formatTimecode, normalizeFps } from '../../utils/timecode';

/** About 20px per frame at 30fps: enough to grab a single frame. */
export const MAX_PIXELS_PER_SECOND = 600;
export const ZOOM_STEP = 1.5;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Zoom that shows the whole duration in the viewport. */
export const fitPixelsPerSecond = (duration: number, viewportWidth: number): number => {
  if (!(duration > 0) || !(viewportWidth > 0)) return 10;
  return viewportWidth / duration;
};

/**
 * Keeps zoom between "whole video fits" and the frame-level maximum. For a
 * clip so short that fitting it already exceeds the maximum, fit wins.
 */
export const clampZoom = (pps: number, fitPps: number, maxPps: number = MAX_PIXELS_PER_SECOND): number => {
  const safeFit = fitPps > 0 && Number.isFinite(fitPps) ? fitPps : 10;
  const min = safeFit;
  const max = Math.max(maxPps, safeFit);
  if (!Number.isFinite(pps) || pps <= 0) return min;
  return clamp(pps, min, max);
};

export const timeToPx = (seconds: number, pps: number): number =>
  Number.isFinite(seconds) && pps > 0 ? seconds * pps : 0;

export const pxToTime = (px: number, pps: number, duration: number): number => {
  if (!(pps > 0) || !Number.isFinite(px)) return 0;
  return clamp(px / pps, 0, Math.max(0, duration));
};

/**
 * Scroll position that keeps `anchorTime` under the same screen x after a
 * zoom change (Ctrl/Cmd + wheel zooms around the cursor).
 */
export const anchoredScrollLeft = (anchorTime: number, anchorViewportX: number, nextPps: number): number =>
  Math.max(0, anchorTime * nextPps - anchorViewportX);

export interface TickSpacing {
  /** Seconds between labelled ticks. */
  major: number;
  /** Seconds between unlabelled ticks (divides `major`). */
  minor: number;
  /** Minor ticks per major tick. */
  subdivisions: number;
}

const SECOND_INTERVALS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400];

/**
 * Labelled ticks at least `minLabelSpacingPx` apart, on "nice" intervals:
 * whole frames when zoomed into a second, whole seconds and minutes beyond.
 */
export const chooseTickSpacing = (pps: number, fps?: number | null, minLabelSpacingPx = 96): TickSpacing => {
  const rate = normalizeFps(fps);
  const frame = 1 / rate;
  const nominal = Math.round(rate);
  // Whole-frame intervals below a second; half a second only when it is a whole
  // number of frames (15 at 30fps, 12 at 24fps, not 12.5 at 25fps).
  const frameMultiples = [1, 2, 5, 10, nominal % 2 === 0 ? nominal / 2 : 0]
    .filter((n, i, all) => n >= 1 && n < nominal && all.indexOf(n) === i)
    .sort((a, b) => a - b);
  const candidates = [...frameMultiples.map((n) => n * frame), ...SECOND_INTERVALS];
  const safePps = pps > 0 && Number.isFinite(pps) ? pps : 1;
  const major = candidates.find((interval) => interval * safePps >= minLabelSpacingPx) ?? candidates[candidates.length - 1];

  // Subdivide so minor ticks are at least 8px apart and never finer than a frame.
  let subdivisions = 1;
  for (const divisions of [10, 6, 5, 4, 3, 2]) {
    const minor = major / divisions;
    const wholeFrames = major < 1 ? Math.abs(minor / frame - Math.round(minor / frame)) < 1e-6 : true;
    if (minor * safePps >= 8 && minor >= frame - 1e-9 && wholeFrames) {
      subdivisions = divisions;
      break;
    }
  }
  return { major, minor: major / subdivisions, subdivisions };
};

const pad2 = (value: number) => String(value).padStart(2, '0');

/**
 * Label for a major ruler tick. On whole-second ticks it reads like a clock,
 * "0:15" or "1:02:05", because a timecode without hours ("00:15:00") reads
 * as fifteen minutes. Zoomed in past a second, where ticks fall between
 * seconds, it is the full timecode ("00:00:15:10"), matching the readout.
 */
export const formatRulerLabel = (seconds: number, fps: number | null | undefined, majorInterval: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  if (majorInterval < 1 - 1e-9) return formatTimecode(safe, normalizeFps(fps));
  const total = Math.round(safe);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor(total / 60) % 60;
  const secs = total % 60;
  return hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(secs)}` : `${minutes}:${pad2(secs)}`;
};

export interface Tick {
  time: number;
  major: boolean;
}

/**
 * Ticks between viewStart and viewEnd (seconds), limited to [0, duration].
 * Only the visible window is generated, so a long video zoomed to frames
 * still renders a few dozen ticks rather than hundreds of thousands.
 */
export const computeTicks = (
  viewStart: number,
  viewEnd: number,
  duration: number,
  spacing: TickSpacing
): Tick[] => {
  if (!(duration > 0) || !(spacing.minor > 0)) return [];
  const start = Math.max(0, viewStart);
  const end = Math.min(duration, viewEnd);
  if (end < start) return [];
  const first = Math.ceil(start / spacing.minor - 1e-9);
  const last = Math.floor(end / spacing.minor + 1e-9);
  const ticks: Tick[] = [];
  // Guard against pathological inputs producing millions of ticks.
  const cap = Math.min(last, first + 5000);
  for (let i = first; i <= cap; i += 1) {
    ticks.push({ time: i * spacing.minor, major: i % spacing.subdivisions === 0 });
  }
  return ticks;
};

export interface PackableItem {
  start: number;
  end?: number | null;
}

export interface RowPackingOptions {
  /** Width a point marker occupies, centred on its time. */
  pointWidthPx?: number;
  /** Narrowest a range is drawn. */
  minBarPx?: number;
  /** Space kept between neighbours in one row. */
  gapPx?: number;
  /** Rows a lane may use; overflow shares the least-crowded row. */
  maxRows?: number;
}

/**
 * Greedy interval packing: each item goes in the first row where it does not
 * overlap the previous item (in pixels at the current zoom). Returns the row
 * of each item, in the input order, and the number of rows used.
 */
export const assignRows = (
  items: PackableItem[],
  pps: number,
  { pointWidthPx = 12, minBarPx = 6, gapPx = 2, maxRows = 4 }: RowPackingOptions = {}
): { rows: number[]; rowCount: number } => {
  const rows = new Array<number>(items.length).fill(0);
  const rowEnds: number[] = [];
  const order = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.start - b.item.start || a.index - b.index);

  for (const { item, index } of order) {
    const isRange = typeof item.end === 'number' && item.end > item.start;
    const left = isRange ? item.start * pps : item.start * pps - pointWidthPx / 2;
    const right = isRange ? Math.max((item.end as number) * pps, left + minBarPx) : left + pointWidthPx;

    let row = rowEnds.findIndex((rowEnd) => rowEnd + gapPx <= left);
    if (row === -1) {
      if (rowEnds.length < Math.max(1, maxRows)) {
        row = rowEnds.length;
        rowEnds.push(right);
      } else {
        // Every row is busy: share the row that frees up first.
        row = rowEnds.indexOf(Math.min(...rowEnds));
        rowEnds[row] = Math.max(rowEnds[row], right);
      }
    } else {
      rowEnds[row] = right;
    }
    rows[index] = row;
  }

  return { rows, rowCount: Math.max(1, rowEnds.length) };
};

/**
 * Snap a dragged time to the nearest candidate (other items' edges, the
 * playhead) within `thresholdPx`, otherwise to the start of its frame.
 */
export const snapTime = (
  seconds: number,
  candidates: number[],
  pps: number,
  fps?: number | null,
  thresholdPx = 6
): number => {
  let best = seconds;
  let bestDistance = thresholdPx;
  for (const candidate of candidates) {
    if (!Number.isFinite(candidate)) continue;
    const distance = Math.abs(candidate - seconds) * pps;
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  if (best !== seconds) return best;
  const rate = normalizeFps(fps);
  return Math.round(seconds * rate) / rate;
};

export type DragMode = 'move' | 'resize-start' | 'resize-end';

/**
 * New start/end for an item being dragged by `deltaSeconds`, kept inside the
 * video and never shorter than `minDuration`.
 */
export const applyDrag = (
  mode: DragMode,
  original: { start: number; end: number },
  deltaSeconds: number,
  duration: number,
  minDuration: number
): { start: number; end: number } => {
  const length = Math.max(0, original.end - original.start);
  const maxEnd = duration > 0 ? duration : Number.POSITIVE_INFINITY;
  if (mode === 'move') {
    const start = clamp(original.start + deltaSeconds, 0, Math.max(0, maxEnd - length));
    return { start, end: start + length };
  }
  if (mode === 'resize-start') {
    const start = clamp(original.start + deltaSeconds, 0, original.end - minDuration);
    return { start, end: original.end };
  }
  const end = clamp(original.end + deltaSeconds, original.start + minDuration, maxEnd);
  return { start: original.start, end };
};
